import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Container, LinearProgress, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import DataTable, { Column, RowAction } from '../components/DataTable';
import { useToast } from '../components/ui/ToastProvider';
import { transactionStore, FileUploadCoreTransaction } from '../utils/transactionStore';
interface FileUploadProps {
  onLogout: () => void;
}
type ValidationStatus = 'Valid' | 'Invalid';
type ProcessingStatus = 'Pending' | 'Success' | 'Failed' | 'Duplicate';
type FileTab = 'uploads' | 'feedback';
interface UploadRow {
  id: string;
  originalTxnDate: string;
  txnRail: string;
  partner: string;
  txnAmount: string;
  cbsReference: string;
  txnReference: string;
  receiverIban: string;
  receiverName: string;
  payoutMethod: string;
  purposeOfPayment: string;
  originatingCountry: string;
  destinationCountry: string;
  payingCurrency: string;
  remitterFullName: string;
  beneficiaryBankCode: string;
  settlementDetails: string;
  walletNumber: string;
  receiverCnic: string;
  validationStatus: ValidationStatus;
  processingStatus: ProcessingStatus;
  responseCode: string;
  responseMessage: string;
  errors: string;
}
interface UploadedFileRecord {
  id: string;
  fileName: string;
  uploadedAt: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  pendingRows: number;
  successRows: number;
  failedRows: number;
  duplicateRows: number;
  status: 'Validated' | 'Needs Fix' | 'Processed';
  retryOf?: string;
  rows: UploadRow[];
}
interface FeedbackFileRecord {
  id: string;
  fileName: string;
  sourceFileName: string;
  generatedAt: string;
  totalRows: number;
  successRows: number;
  failedRows: number;
  pendingRows: number;
  duplicateRows: number;
  status: 'Ready';
  rows: unknown[][];
}
const requiredHeaders = ['Original Txn Date', 'Txn Rail', 'Partner', 'Txn Amount', 'CBS Reference', 'Txn Reference', 'Receiver IBAN', 'Receiver Name', 'Payout Method', 'Purpose of Payment', 'Originating Country', 'Destination Country', 'Paying Currency', 'Remitter Full Name', 'Beneficiary Bank Code (IMD)', 'Settlement Details', 'Wallet Number', 'Receiver CNIC'];
const validPurposes = ['Family Support', 'Salary', 'Education', 'Medical', 'Business Payment', 'Gift'];
const validCurrencies = ['PKR', 'USD', 'AED', 'SAR', 'GBP', 'EUR'];
const validCountries = ['PK', 'US', 'AE', 'SA', 'GB', 'CA'];
const demoImdCodes = ['220583', '588974', '999001', '505439'];
type FileUploadTransactionStore = typeof transactionStore & {
  hasProcessedReference?: (partner: string, txnReference: string) => boolean;
  addFileUploadTransactions?: (transactions: FileUploadCoreTransaction[]) => void;
};
const fileUploadTransactionStore = transactionStore as FileUploadTransactionStore;
const demoRows = Array.from({
  length: 20
}, (_, index) => {
  const method = index % 3 === 0 ? 'ACCOUNT' : index % 3 === 1 ? 'CASH' : 'WALLET';
  const ref = index === 12 ? 'RIA-DUP-0001' : index === 15 ? 'RIA-FILE-0004' : `RIA-FILE-${String(index + 1).padStart(4, '0')}`;
  return ['2026-06-30', index === 6 ? 'BADRAIL' : index % 4 === 0 ? '1LINK' : index % 4 === 1 ? 'RAAST' : 'RTGS', 'RIA Money Transfer', index === 7 ? '-2000' : String(25000 + index * 4500), method === 'ACCOUNT' || index % 2 === 0 ? `CBS2026${String(index + 1).padStart(5, '0')}` : '', ref, index === 5 ? 'PK-BAD-IBAN' : `PK36BIPL000000${String(1234567890 + index).padStart(10, '0')}`, `Receiver ${index + 1}`, method, index === 8 ? '' : validPurposes[index % validPurposes.length], index === 9 ? '' : validCountries[(index + 1) % validCountries.length], 'PK', index === 10 ? 'XXX' : 'PKR', index === 11 ? '' : `Remitter ${index + 1}`, method === 'ACCOUNT' && index !== 4 ? demoImdCodes[index % demoImdCodes.length] : '', index % 5 === 0 ? 'Same-day settlement' : '', method === 'WALLET' && index !== 14 ? `03${String(100000000 + index).slice(0, 9)}` : '', method === 'CASH' && index !== 13 ? `35202-1234567-${index % 10}` : ''];
});
const correctedDemoRows = demoRows.map((row, index) => {
  const corrected = [...row];
  corrected[1] = ['1LINK', 'RAAST', 'RTGS'][index % 3];
  corrected[3] = String(25000 + index * 4500);
  corrected[5] = `RIA-CORR-${String(index + 1).padStart(4, '0')}`;
  corrected[6] = `PK36BIPL000000${String(2234567890 + index).padStart(10, '0')}`;
  corrected[9] = validPurposes[index % validPurposes.length];
  corrected[10] = validCountries[(index + 1) % validCountries.length];
  corrected[12] = 'PKR';
  corrected[13] = `Remitter ${index + 1}`;
  if (corrected[8] === 'ACCOUNT') corrected[14] = demoImdCodes[index % demoImdCodes.length];
  if (corrected[8] === 'WALLET') corrected[16] = `03${String(200000000 + index).slice(0, 9)}`;
  if (corrected[8] === 'CASH') corrected[17] = `35202-7654321-${index % 10}`;
  return corrected;
});
const validDemoRows = correctedDemoRows.slice(0, 8).map((row, index) => {
  const validRow = [...row];
  validRow[5] = `RIA-VALID-${String(index + 1).padStart(4, '0')}`;
  return validRow;
});
const feedbackHeaders = ['Txn Reference', 'Partner', 'Validation Status', 'Processing Status', 'Response Code', 'Response Message', 'Source', 'Processed At'];
const csvEscape = (value: unknown) => {
  const text = value === undefined || value === null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};
const downloadCsv = (filename: string, rows: unknown[][]) => {
  const csv = rows.map(row => row.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const parseCsv = (text: string) => {
  const rows: string[][] = [];
  let current = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      row.push(current.trim());
      current = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1;
      row.push(current.trim());
      if (row.some(cell => cell.length > 0)) rows.push(row);
      row = [];
      current = '';
    } else {
      current += char;
    }
  }
  row.push(current.trim());
  if (row.some(cell => cell.length > 0)) rows.push(row);
  return rows;
};
const cleanCell = (value: string) => value.replace(/^\uFEFF/, '').trim();
const isValidDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(value).getTime());
const isValidIban = (value: string) => /^PK[0-9A-Z]{22}$/i.test(value);
const isValidImd = (value: string) => /^\d{6}$/.test(value);
const responseScenario = (row: UploadRow, index: number) => {
  if (row.validationStatus === 'Invalid') {
    return {
      processingStatus: 'Failed' as ProcessingStatus,
      responseCode: 'V001',
      responseMessage: row.errors
    };
  }
  if (fileUploadTransactionStore.hasProcessedReference?.(row.partner, row.txnReference)) {
    return {
      processingStatus: 'Duplicate' as ProcessingStatus,
      responseCode: 'DUP01',
      responseMessage: 'Duplicate transaction reference - already processed'
    };
  }
  const scenarios = [{
    processingStatus: 'Success' as ProcessingStatus,
    responseCode: '00',
    responseMessage: 'Transaction processed successfully'
  }, {
    processingStatus: 'Failed' as ProcessingStatus,
    responseCode: 'AML01',
    responseMessage: 'Transaction held for AML review - rejected'
  }, {
    processingStatus: 'Failed' as ProcessingStatus,
    responseCode: 'BAL01',
    responseMessage: 'Insufficient partner balance'
  }, {
    processingStatus: 'Failed' as ProcessingStatus,
    responseCode: 'CBS01',
    responseMessage: 'CBS processing failure - bank rejected'
  }, {
    processingStatus: 'Pending' as ProcessingStatus,
    responseCode: 'P00',
    responseMessage: 'Transaction under processing'
  }];
  return scenarios[index % scenarios.length];
};
const validateRows = (parsedRows: string[][]) => {
  if (parsedRows.length === 0) return {
    headerErrors: ['CSV file is empty.'],
    rows: [] as UploadRow[]
  };
  const headers = parsedRows[0].map(header => cleanCell(header).toLowerCase());
  const missing = requiredHeaders.filter(header => !headers.includes(header.toLowerCase()));
  const headerErrors = missing.length > 0 ? [`Missing required columns: ${missing.join(', ')}`] : [];
  const headerIndex = (header: string) => headers.indexOf(header.toLowerCase());
  const seenRefs = new Set<string>();
  const rows = parsedRows.slice(1).map((cells, index) => {
    const get = (header: string) => cleanCell(cells[headerIndex(header)] || '');
    const rowBase = {
      id: `ROW-${index + 1}`,
      originalTxnDate: get('Original Txn Date'),
      txnRail: get('Txn Rail'),
      partner: get('Partner'),
      txnAmount: get('Txn Amount'),
      cbsReference: get('CBS Reference'),
      txnReference: get('Txn Reference'),
      receiverIban: get('Receiver IBAN'),
      receiverName: get('Receiver Name'),
      payoutMethod: get('Payout Method').toUpperCase(),
      purposeOfPayment: get('Purpose of Payment'),
      originatingCountry: get('Originating Country').toUpperCase(),
      destinationCountry: get('Destination Country').toUpperCase(),
      payingCurrency: get('Paying Currency').toUpperCase(),
      remitterFullName: get('Remitter Full Name'),
      beneficiaryBankCode: get('Beneficiary Bank Code (IMD)').toUpperCase(),
      settlementDetails: get('Settlement Details'),
      walletNumber: get('Wallet Number'),
      receiverCnic: get('Receiver CNIC')
    };
    const errors: string[] = [];
    const rail = rowBase.txnRail.toUpperCase();
    const refKey = `${rowBase.partner}:${rowBase.txnReference}`;
    if (!isValidDate(rowBase.originalTxnDate)) errors.push('Original Txn Date must be YYYY-MM-DD');
    if (!['1LINK', 'RAAST', 'RTGS'].includes(rail)) errors.push('Txn Rail must be 1LINK, RAAST, or RTGS');
    if (!rowBase.partner) errors.push('Partner is required');
    if (!rowBase.txnAmount || Number.isNaN(Number(rowBase.txnAmount)) || Number(rowBase.txnAmount) <= 0) errors.push('Txn Amount must be a positive number');
    if (rail !== 'RTGS' && !rowBase.cbsReference) errors.push('CBS Reference is required for 1LINK and RAAST');
    if (!rowBase.txnReference) errors.push('Txn Reference is required');
    if (seenRefs.has(refKey)) errors.push('Duplicate Txn Reference inside uploaded file');
    if (rowBase.txnReference) seenRefs.add(refKey);
    if (!isValidIban(rowBase.receiverIban)) errors.push('Receiver IBAN must be a Pakistani IBAN');
    if (!rowBase.receiverName) errors.push('Receiver Name is required');
    if (!['ACCOUNT', 'CASH', 'WALLET'].includes(rowBase.payoutMethod)) errors.push('Payout Method must be ACCOUNT, CASH, or WALLET');
    if (!validPurposes.includes(rowBase.purposeOfPayment)) errors.push('Purpose of Payment is missing or invalid');
    if (!validCountries.includes(rowBase.originatingCountry)) errors.push('Originating Country is required or invalid');
    if (!validCountries.includes(rowBase.destinationCountry)) errors.push('Destination Country is required or invalid');
    if (!validCurrencies.includes(rowBase.payingCurrency)) errors.push('Paying Currency is required or invalid');
    if (!rowBase.remitterFullName) errors.push('Remitter Full Name is mandatory');
    if (rowBase.payoutMethod === 'ACCOUNT' && !rowBase.beneficiaryBankCode) errors.push('Beneficiary Bank Code (IMD) is mandatory for ACCOUNT payout');
    if (rowBase.payoutMethod === 'ACCOUNT' && rowBase.beneficiaryBankCode && !isValidImd(rowBase.beneficiaryBankCode)) errors.push('Beneficiary Bank Code (IMD) must be a 6 digit code');
    if (rowBase.payoutMethod === 'WALLET' && !rowBase.walletNumber) errors.push('Wallet Number is mandatory for WALLET payout');
    if (rowBase.payoutMethod === 'CASH' && !rowBase.receiverCnic) errors.push('Receiver CNIC is mandatory for CASH payout');
    const validationStatus: ValidationStatus = errors.length === 0 ? 'Valid' : 'Invalid';
    const row = {
      ...rowBase,
      validationStatus,
      errors: errors.join('; ')
    } as UploadRow;
    const scenario = responseScenario(row, index);
    return {
      ...row,
      ...scenario
    };
  });
  return {
    headerErrors,
    rows
  };
};
const toUploadCsvRows = (rows: UploadRow[]) => [requiredHeaders, ...rows.map(row => [row.originalTxnDate, row.txnRail, row.partner, row.txnAmount, row.cbsReference, row.txnReference, row.receiverIban, row.receiverName, row.payoutMethod, row.purposeOfPayment, row.originatingCountry, row.destinationCountry, row.payingCurrency, row.remitterFullName, row.beneficiaryBankCode, row.settlementDetails, row.walletNumber, row.receiverCnic])];
const buildFeedbackRows = (rows: UploadRow[]) => [feedbackHeaders, ...rows.map(row => [row.txnReference, row.partner, row.validationStatus, row.processingStatus, row.responseCode, row.responseMessage, 'File Upload', new Date().toLocaleString()])];
const buildFailedRows = (rows: UploadRow[]) => [[...requiredHeaders, 'Validation Status', 'Processing Status', 'Response Code', 'Response Message'], ...rows.filter(row => row.validationStatus === 'Invalid' || row.processingStatus === 'Failed' || row.processingStatus === 'Duplicate').map(row => [...toUploadCsvRows([row])[1], row.validationStatus, row.processingStatus, row.responseCode, row.responseMessage])];
const toCoreTransactions = (rows: UploadRow[], uploadBatchId: string): FileUploadCoreTransaction[] => rows.map((row, index) => {
  const failed = row.validationStatus === 'Invalid' || row.processingStatus === 'Failed' || row.processingStatus === 'Duplicate';
  const pending = row.processingStatus === 'Pending';
  const payoutType = row.payoutMethod === 'CASH' ? 'OTC' : row.txnRail.toUpperCase() === 'RTGS' ? 'FT' : 'IBFT';
  return {
    id: `FILE-${uploadBatchId}-${index + 1}`,
    mtoName: row.partner || 'RIA Money Transfer',
    mtoRef: row.txnReference || `MISSING-${index + 1}`,
    bankRef: row.cbsReference || `FILE-${index + 1}`,
    branchCode: row.payoutMethod === 'CASH' ? 'PORTAL' : '',
    agentCode: row.payoutMethod === 'CASH' ? 'FILE' : '',
    payoutType,
    amountFCY: row.txnAmount || '0',
    currencyFCY: row.payingCurrency || 'PKR',
    amountPKR: Number(row.txnAmount || 0).toLocaleString(),
    corridor: `${row.destinationCountry || 'PK'}-${row.originatingCountry || 'US'}`,
    purposeCode: row.purposeOfPayment.slice(0, 3).toUpperCase() || 'UNK',
    purposeText: row.purposeOfPayment || 'Unknown',
    currentState: failed ? row.validationStatus === 'Invalid' ? 'SCREENING' : 'FT_POST' : pending ? 'FT_POST' : 'SUCCESS',
    lifecycleStatus: failed ? 'FAILED' : pending ? 'PENDING_INQUIRY' : 'SUCCESS',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    remitterName: row.remitterFullName || 'Missing remitter',
    beneficiaryName: row.receiverName || 'Missing beneficiary',
    beneficiaryIBAN: row.receiverIban,
    beneficiaryBankName: row.beneficiaryBankCode,
    railRefs: row.processingStatus === 'Success' ? {
      RRN: `FILE-RRN-${index + 1}`,
      STAN: `F${index + 1}`
    } : {},
    stateHistory: [{
      stateCode: 'SCREENING',
      status: row.validationStatus === 'Valid' ? 'PASS' : 'FAIL',
      timestamp: new Date().toISOString(),
      failReasonCode: row.validationStatus === 'Invalid' ? row.responseCode : undefined
    }, {
      stateCode: failed ? 'FT_POST' : 'SUCCESS',
      status: failed ? 'FAIL' : pending ? 'PENDING' : 'PASS',
      timestamp: new Date().toISOString(),
      failReasonCode: failed ? row.responseCode : undefined
    }],
    source: 'File Upload',
    uploadBatchId,
    validationStatus: row.validationStatus,
    processingStatus: row.processingStatus,
    failureReason: row.responseMessage,
    responseCode: row.responseCode
  };
});
const buildUploadRecord = (fileName: string, rows: UploadRow[], uploadedAt: string, id: string, retryOf?: string): UploadedFileRecord => {
  const validRows = rows.filter(row => row.validationStatus === 'Valid').length;
  const invalidRows = rows.length - validRows;
  const successRows = rows.filter(row => row.processingStatus === 'Success').length;
  const failedRows = rows.filter(row => row.processingStatus === 'Failed').length;
  const pendingRows = rows.filter(row => row.processingStatus === 'Pending').length;
  const duplicateRows = rows.filter(row => row.processingStatus === 'Duplicate').length;
  return {
    id,
    fileName,
    uploadedAt,
    totalRows: rows.length,
    validRows,
    invalidRows,
    pendingRows,
    successRows,
    failedRows,
    duplicateRows,
    status: invalidRows > 0 || failedRows > 0 || duplicateRows > 0 ? 'Needs Fix' : pendingRows > 0 ? 'Validated' : 'Processed',
    retryOf,
    rows
  };
};
const buildFeedbackRecord = (uploadRecord: UploadedFileRecord): FeedbackFileRecord => ({
  id: `${uploadRecord.id}-FEEDBACK`,
  fileName: `${uploadRecord.fileName.replace(/\.[^.]+$/, '')}-feedback.csv`,
  sourceFileName: uploadRecord.fileName,
  generatedAt: uploadRecord.uploadedAt,
  totalRows: uploadRecord.totalRows,
  successRows: uploadRecord.successRows,
  failedRows: uploadRecord.failedRows,
  pendingRows: uploadRecord.pendingRows,
  duplicateRows: uploadRecord.duplicateRows,
  status: 'Ready',
  rows: buildFeedbackRows(uploadRecord.rows)
});
const demoUploadRows = validateRows([requiredHeaders, ...demoRows.slice(0, 10)]).rows;
const demoValidUploadRows = validateRows([requiredHeaders, ...validDemoRows.slice(0, 6)]).rows;
const demoCorrectedUploadRows = validateRows([requiredHeaders, ...correctedDemoRows.slice(0, 8)]).rows;
const initialUploadedFiles = [buildUploadRecord('transaction-upload-demo-mixed.csv', demoUploadRows, '2026-07-10T09:15:00.000Z', 'DEMO-MIXED'), buildUploadRecord('transaction-upload-demo-valid.csv', demoValidUploadRows, '2026-07-10T09:05:00.000Z', 'DEMO-VALID'), buildUploadRecord('transaction-upload-demo-corrected.csv', demoCorrectedUploadRows, '2026-07-10T08:42:00.000Z', 'DEMO-CORRECTED', 'transaction-upload-demo-mixed.csv')];
const initialFeedbackFiles = initialUploadedFiles.map(buildFeedbackRecord);
const FileUpload: React.FC<FileUploadProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FileTab>('uploads');
  const [fileName, setFileName] = useState('');
  const [headerErrors, setHeaderErrors] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileRecord[]>(initialUploadedFiles);
  const [feedbackFiles, setFeedbackFiles] = useState<FeedbackFileRecord[]>(initialFeedbackFiles);
  const [isReading, setIsReading] = useState(false);
  const [retryParent, setRetryParent] = useState<UploadedFileRecord | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    showToast
  } = useToast();
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const cardSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    bgcolor: theme.palette.background.paper,
    boxShadow: 'none'
  };
  const handleDownloadSample = () => downloadCsv('transaction-upload-demo-mixed.csv', [requiredHeaders, ...demoRows]);
  const handleDownloadValidDemo = () => downloadCsv('transaction-upload-demo-valid.csv', [requiredHeaders, ...validDemoRows]);
  const handleDownloadCorrectedDemo = () => downloadCsv('transaction-upload-demo-corrected.csv', [requiredHeaders, ...correctedDemoRows]);
  const handleUploadClick = (parent?: UploadedFileRecord) => {
    setRetryParent(parent || null);
    fileInputRef.current?.click();
  };
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsReading(true);
    setFileName(file.name);
    try {
      const text = await file.text();
      const result = validateRows(parseCsv(text));
      if (result.headerErrors.length > 0 && result.rows.length === 0) {
        setHeaderErrors(result.headerErrors);
        showToast('File columns need attention before rows can be imported', 'warning');
        return;
      }
      const id = `${retryParent ? 'RETRY' : 'UPLOAD'}-${Date.now()}`;
      const uploadedAt = new Date().toISOString();
      const baseName = file.name.replace(/\.[^.]+$/, '');
      const uploadRecord = buildUploadRecord(file.name, result.rows, uploadedAt, id, retryParent?.fileName);
      const feedbackRecord = buildFeedbackRecord(uploadRecord);
      setUploadedFiles(prev => [uploadRecord, ...prev]);
      setFeedbackFiles(prev => [{
        ...feedbackRecord,
        fileName: `${baseName}-feedback.csv`
      }, ...prev]);
      const nextHeaderErrors = [...result.headerErrors];
      try {
        fileUploadTransactionStore.addFileUploadTransactions?.(toCoreTransactions(result.rows, id));
      } catch (syncError) {
        const message = syncError instanceof Error ? syncError.message : 'Unknown transaction table sync error';
        nextHeaderErrors.push(`File was validated and feedback was generated, but system table sync failed: ${message}`);
      }
      setHeaderErrors(nextHeaderErrors);
      showToast(`${file.name} uploaded. Feedback generated.`, nextHeaderErrors.length || uploadRecord.invalidRows || uploadRecord.failedRows ? 'warning' : 'success');
      setActiveTab('uploads');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown CSV parsing error';
      setHeaderErrors([`Unable to process the selected CSV file: ${message}`]);
      showToast('Unable to process the selected CSV file', 'error');
    } finally {
      setIsReading(false);
      setRetryParent(null);
      if (event.target) event.target.value = '';
    }
  };
  const uploadedColumns: Column<UploadedFileRecord>[] = [{
    id: 'fileName',
    label: 'File Name',
    renderCell: row => <Typography variant="body2" fontWeight={800} data-id="element-3584" data-mp="3g8q19adsd6ea">{row.fileName}</Typography>
  }, {
    id: 'retryOf',
    label: 'Retry Of',
    renderCell: row => <Typography variant="body2" color="text.secondary" data-id="element-3585" data-mp="2nquh7lb5ohav">{row.retryOf || '-'}</Typography>
  }, {
    id: 'uploadedAt',
    label: 'Uploaded At',
    renderCell: row => <Typography variant="body2" data-id="element-3586" data-mp="2g61sc3otibl3">{new Date(row.uploadedAt).toLocaleString()}</Typography>
  }, {
    id: 'totalRows',
    label: 'Rows'
  }, {
    id: 'validRows',
    label: 'Valid'
  }, {
    id: 'invalidRows',
    label: 'Invalid'
  }, {
    id: 'successRows',
    label: 'Success'
  }, {
    id: 'failedRows',
    label: 'Failed'
  }, {
    id: 'duplicateRows',
    label: 'Duplicate'
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status} size="small" color={row.status === 'Processed' ? 'success' : row.status === 'Validated' ? 'info' : 'warning'} variant="outlined" sx={{
      fontWeight: 800
    }} data-id="element-3587" data-mp="1xa1tqa1c49sj" />
  }];
  const feedbackColumns: Column<FeedbackFileRecord>[] = [{
    id: 'fileName',
    label: 'Feedback File',
    renderCell: row => <Typography variant="body2" fontWeight={800} data-id="element-3588" data-mp="1jiwzxoknebq4">{row.fileName}</Typography>
  }, {
    id: 'sourceFileName',
    label: 'Source Txn File'
  }, {
    id: 'generatedAt',
    label: 'Generated At',
    renderCell: row => <Typography variant="body2" data-id="element-3589" data-mp="19kzgdedqh33u">{new Date(row.generatedAt).toLocaleString()}</Typography>
  }, {
    id: 'successRows',
    label: 'Success'
  }, {
    id: 'failedRows',
    label: 'Failed'
  }, {
    id: 'pendingRows',
    label: 'Pending'
  }, {
    id: 'duplicateRows',
    label: 'Duplicate'
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status} size="small" color="success" variant="outlined" sx={{
      fontWeight: 800
    }} data-id="element-3590" data-mp="34ufiw6dtfj70" />
  }];
  const uploadedActions: RowAction<UploadedFileRecord>[] = [{
    icon: 'lucide:download',
    label: 'Download Txn File',
    onClick: row => downloadCsv(row.fileName, toUploadCsvRows(row.rows))
  }, {
    icon: 'lucide:file-warning',
    label: 'Download Failed Rows',
    onClick: row => downloadCsv(`${row.fileName.replace(/\.[^.]+$/, '')}-failed-rows.csv`, buildFailedRows(row.rows)),
    condition: row => row.invalidRows + row.failedRows + row.duplicateRows > 0
  }, {
    icon: 'lucide:refresh-cw',
    label: 'Re-upload Corrected File',
    onClick: row => handleUploadClick(row),
    condition: row => row.invalidRows + row.failedRows + row.duplicateRows > 0
  }, {
    icon: 'lucide:file-check',
    label: 'Download Corrected Demo',
    onClick: row => downloadCsv(`${row.fileName.replace(/\.[^.]+$/, '')}-corrected-demo.csv`, [requiredHeaders, ...correctedDemoRows]),
    condition: row => row.invalidRows + row.failedRows + row.duplicateRows > 0
  }];
  const feedbackActions: RowAction<FeedbackFileRecord>[] = [{
    icon: 'lucide:download',
    label: 'Download Feedback File',
    onClick: row => downloadCsv(row.fileName, row.rows)
  }];
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    bgcolor: 'background.default'
  }} data-id="element-3591" data-mp="2icueeaxl4jsw">
      <TopBar onMenuClick={() => setMobileOpen(!mobileOpen)} onLogout={onLogout} data-id="element-3592" data-mp="2x9v8um75usv2" />
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage="file-upload" data-id="element-3593" data-mp="2sba9sq3naf87" />
      <Box component="main" sx={{
      flexGrow: 1,
      px: {
        xs: 1.2,
        md: 2
      },
      py: 1.2,
      width: {
        sm: 'calc(100% - 240px)'
      },
      mt: '62px'
    }} data-id="element-3594" data-mp="12wdh0mzq39u8">
        <Container maxWidth={false} disableGutters={isMobile} data-id="element-3595" data-mp="xz3txdzt3a9k">
          <Card elevation={0} sx={{
          ...cardSx,
          mb: 2
        }} data-id="element-3596" data-mp="1nmg0r4zfdr2k">
            <CardContent sx={{
            p: '20px',
            '&:last-child': {
              pb: '20px'
            }
          }} data-id="element-3597" data-mp="1no0wuz1e1i2j">
              <Stack direction={{
              xs: 'column',
              lg: 'row'
            }} alignItems={{
              xs: 'stretch',
              lg: 'center'
            }} justifyContent="space-between" spacing={2} data-id="element-3598" data-mp="1ro1m7eq0zdod">
                <Box data-id="element-3599" data-mp="21lfqyh2l6e0u">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{
                  mb: 0.5
                }} data-id="element-3600" data-mp="21lz6xs8oesc8">
                    <Icon icon="lucide:upload-cloud" width={20} color={theme.palette.primary.main} data-id="element-3601" data-mp="26lbs88jwfrcp" />
                    <Typography sx={{
                    color: textPrimary,
                    fontSize: '1.15rem',
                    fontWeight: 950
                  }} data-id="element-3602" data-mp="2alm05qr94ru7">Transaction File Upload</Typography>
                  </Stack>
                  <Typography sx={{
                  color: textSecondary,
                  fontSize: '0.82rem',
                  fontWeight: 750
                }} data-id="element-3603" data-mp="2ffgfyj3qkmo0">
                    Upload Push API-aligned transaction CSVs, validate them, generate feedback, and push all rows into Transactions and Failed Transactions.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{
                xs: 'flex-start',
                lg: 'flex-end'
              }} sx={{
                maxWidth: {
                  lg: 520
                },
                rowGap: 1
              }} data-id="element-3604" data-mp="in1c5c2ke2oh">
                  <Button startIcon={<Icon icon="lucide:file-check" data-id="element-3606" data-mp="dq17n5ld57zh" />} variant="outlined" onClick={handleDownloadValidDemo} data-id="element-3605" data-mp="dmdcy346mfj6">Valid Demo</Button>
                  <Button startIcon={<Icon icon="lucide:file-warning" data-id="element-3608" data-mp="3qk1kn278s2z" />} variant="outlined" onClick={handleDownloadSample} data-id="element-3607" data-mp="3q0i31wwhvmu">Mixed Demo</Button>
                  <Button startIcon={<Icon icon="lucide:download" data-id="element-3610" data-mp="11cr3tqb168c6" />} variant="outlined" onClick={handleDownloadCorrectedDemo} data-id="element-3609" data-mp="11c7o8g8urw1b">Corrected Demo</Button>
                  <Button startIcon={<Icon icon="lucide:upload" data-id="element-3612" data-mp="rn7jshbl08fq" />} variant="contained" onClick={() => handleUploadClick()} data-id="element-3611" data-mp="rnpq66ryk31d">Upload Txn File</Button>
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} data-id="element-3613" data-mp="mnv188rqtniy" />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {isReading && <LinearProgress sx={{
          mb: 2
        }} data-id="element-3614" data-mp="1fbz6wuabe0ln" />}
          {fileName && <Alert severity={headerErrors.length > 0 ? 'warning' : 'success'} sx={{
          mb: 2,
          borderRadius: '10px'
        }} data-id="element-3615" data-mp="nph15q4bv69x">{fileName} uploaded. Feedback generated and rows are visible in system tables.</Alert>}
          {headerErrors.map(error => <Alert key={error} severity="error" sx={{
          mb: 2,
          borderRadius: '10px'
        }} data-id="element-3616" data-mp="eqluye1a9w6m">{error}</Alert>)}

          <Card elevation={0} sx={{
          ...cardSx
        }} data-id="element-3617" data-mp="joncguj29nfz">
            <CardContent sx={{
            p: 1.5,
            '&:last-child': {
              pb: 1.5
            }
          }} data-id="element-3618" data-mp="17dzl9tah7thr">
              <Tabs value={activeTab} onChange={(_, value: FileTab) => setActiveTab(value)} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons={isMobile ? 'auto' : false} sx={{
              mb: 1.5,
              minHeight: 42,
              '& .MuiTab-root': {
                minHeight: 42,
                textTransform: 'none',
                fontWeight: 850
              }
            }} data-id="element-3619" data-mp="17blong05e983">
                <Tab value="uploads" label={`Uploaded Txn Files (${uploadedFiles.length})`} icon={<Icon icon="lucide:file-up" width={17} data-id="element-3621" data-mp="3i46g5pv9rtie" />} iconPosition="start" data-id="element-3620" data-mp="12gpy6kak7tx3" />
                <Tab value="feedback" label={`Feedback Files (${feedbackFiles.length})`} icon={<Icon icon="lucide:file-down" width={17} data-id="element-3623" data-mp="3fktlapt374n5" />} iconPosition="start" data-id="element-3622" data-mp="3n5vqt4eky55y" />
              </Tabs>
              {activeTab === 'uploads' && <DataTable title="Uploaded Transaction Files" data={uploadedFiles} columns={uploadedColumns} rowActions={uploadedActions} keyField="id" showColumnFilters={false} emptyStateMessage="No transaction files uploaded yet" initialVisibleColumns={['fileName', 'retryOf', 'uploadedAt', 'totalRows', 'validRows', 'invalidRows', 'successRows', 'failedRows', 'duplicateRows', 'status']} data-id="element-3624" data-mp="3algp3egbsl7w" />}
              {activeTab === 'feedback' && <DataTable title="Downloadable Feedback Files" data={feedbackFiles} columns={feedbackColumns} rowActions={feedbackActions} keyField="id" showColumnFilters={false} emptyStateMessage="Upload a transaction file to generate feedback" initialVisibleColumns={['fileName', 'sourceFileName', 'generatedAt', 'successRows', 'failedRows', 'pendingRows', 'duplicateRows', 'status']} data-id="element-3625" data-mp="2rxleg3g0fnj0" />}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>;
};
export default FileUpload;